/**
 * Analytics data generators for new BFF API spec
 */

import { faker } from '@faker-js/faker'
import { getTransactions } from './transactions'
import type {
  AnalyticsSummary,
  ApprovalRateDashboard,
  ApprovalRateSimple
} from '../../shared/types/analytics'

faker.seed(99999)

/**
 * Generate analytics summary from transactions
 */
export function generateAnalyticsSummary(
  dateStart?: string,
  dateEnd?: string
): AnalyticsSummary {
  const transactions = getTransactions()

  // Filter by date range if provided
  let filtered = transactions
  if (dateStart || dateEnd) {
    filtered = transactions.filter((t) => {
      const created = new Date(t.created_at)
      if (dateStart && created < new Date(dateStart)) return false
      if (dateEnd && created > new Date(dateEnd)) return false
      return true
    })
  }

  // Calculate status breakdowns
  const paid = filtered.filter(t => t.status === 'paid' || t.status === 'confirmed')
  const pending = filtered.filter(t => t.status === 'pending')
  const error = filtered.filter(t => t.status === 'error' || t.status === 'canceled')

  const paidVolume = paid.reduce((sum, t) => sum + t.usd_amount, 0)
  const pendingVolume = pending.reduce((sum, t) => sum + t.usd_amount, 0)
  const errorVolume = error.reduce((sum, t) => sum + t.usd_amount, 0)

  // Volume by currency
  const volumeByCurrency: Record<string, number> = {}
  for (const t of filtered) {
    volumeByCurrency[t.currency] = (volumeByCurrency[t.currency] || 0) + t.amount
  }

  // Top/worst sales
  const sortedByAmount = [...filtered].sort((a, b) => b.usd_amount - a.usd_amount)
  const topSale = sortedByAmount[0]
  const worstSale = sortedByAmount[sortedByAmount.length - 1]

  // Payment methods
  const paymentMethodMap = new Map<string, { quantity: number, volume_usd: number }>()
  for (const t of filtered) {
    const current = paymentMethodMap.get(t.type_charge) || { quantity: 0, volume_usd: 0 }
    paymentMethodMap.set(t.type_charge, {
      quantity: current.quantity + 1,
      volume_usd: current.volume_usd + t.usd_amount
    })
  }

  const payment_methods = Array.from(paymentMethodMap.entries()).map(([type, data]) => ({
    type,
    ...data
  })).sort((a, b) => b.volume_usd - a.volume_usd)

  // Volume by flag (card brand)
  const flagMap = new Map<string, { quantity: number, volume_usd: number }>()
  for (const t of filtered.filter(t => t.card_brand)) {
    const brand = t.card_brand.charAt(0).toUpperCase() + t.card_brand.slice(1)
    const current = flagMap.get(brand) || { quantity: 0, volume_usd: 0 }
    flagMap.set(brand, {
      quantity: current.quantity + 1,
      volume_usd: current.volume_usd + t.usd_amount
    })
  }

  const volume_by_flag = Array.from(flagMap.entries()).map(([flag, data]) => ({
    flag,
    ...data
  })).sort((a, b) => b.volume_usd - a.volume_usd)

  // Top BINs (mock data) - expanded with more countries for better map visualization
  const top_bins = [
    {
      bin: '411111',
      country: 'Brazil',
      country_code: 'BR',
      brand: 'Visa',
      issuer: 'Banco do Brasil',
      quantity: faker.number.int({ min: 80, max: 200 }),
      volume_usd: faker.number.float({ min: 25000, max: 65000, fractionDigits: 2 })
    },
    {
      bin: '520000',
      country: 'Brazil',
      country_code: 'BR',
      brand: 'Mastercard',
      issuer: 'Itaú',
      quantity: faker.number.int({ min: 60, max: 150 }),
      volume_usd: faker.number.float({ min: 18000, max: 50000, fractionDigits: 2 })
    },
    {
      bin: '376411',
      country: 'United States',
      country_code: 'US',
      brand: 'Amex',
      issuer: 'American Express',
      quantity: faker.number.int({ min: 50, max: 120 }),
      volume_usd: faker.number.float({ min: 20000, max: 55000, fractionDigits: 2 })
    },
    {
      bin: '453211',
      country: 'United States',
      country_code: 'US',
      brand: 'Visa',
      issuer: 'Chase Bank',
      quantity: faker.number.int({ min: 40, max: 100 }),
      volume_usd: faker.number.float({ min: 15000, max: 45000, fractionDigits: 2 })
    },
    {
      bin: '491234',
      country: 'United Kingdom',
      country_code: 'GB',
      brand: 'Visa',
      issuer: 'Barclays',
      quantity: faker.number.int({ min: 35, max: 90 }),
      volume_usd: faker.number.float({ min: 12000, max: 38000, fractionDigits: 2 })
    },
    {
      bin: '542789',
      country: 'Germany',
      country_code: 'DE',
      brand: 'Mastercard',
      issuer: 'Deutsche Bank',
      quantity: faker.number.int({ min: 30, max: 80 }),
      volume_usd: faker.number.float({ min: 10000, max: 35000, fractionDigits: 2 })
    },
    {
      bin: '356789',
      country: 'Japan',
      country_code: 'JP',
      brand: 'JCB',
      issuer: 'Sumitomo Mitsui',
      quantity: faker.number.int({ min: 25, max: 70 }),
      volume_usd: faker.number.float({ min: 8000, max: 28000, fractionDigits: 2 })
    },
    {
      bin: '623456',
      country: 'China',
      country_code: 'CN',
      brand: 'UnionPay',
      issuer: 'ICBC',
      quantity: faker.number.int({ min: 45, max: 110 }),
      volume_usd: faker.number.float({ min: 16000, max: 42000, fractionDigits: 2 })
    },
    {
      bin: '489012',
      country: 'Australia',
      country_code: 'AU',
      brand: 'Visa',
      issuer: 'Commonwealth Bank',
      quantity: faker.number.int({ min: 20, max: 60 }),
      volume_usd: faker.number.float({ min: 7000, max: 25000, fractionDigits: 2 })
    },
    {
      bin: '531234',
      country: 'Canada',
      country_code: 'CA',
      brand: 'Mastercard',
      issuer: 'TD Bank',
      quantity: faker.number.int({ min: 28, max: 75 }),
      volume_usd: faker.number.float({ min: 9000, max: 30000, fractionDigits: 2 })
    },
    {
      bin: '405678',
      country: 'France',
      country_code: 'FR',
      brand: 'Visa',
      issuer: 'BNP Paribas',
      quantity: faker.number.int({ min: 22, max: 65 }),
      volume_usd: faker.number.float({ min: 8500, max: 27000, fractionDigits: 2 })
    },
    {
      bin: '498765',
      country: 'Singapore',
      country_code: 'SG',
      brand: 'Visa',
      issuer: 'DBS Bank',
      quantity: faker.number.int({ min: 18, max: 55 }),
      volume_usd: faker.number.float({ min: 6500, max: 22000, fractionDigits: 2 })
    },
    {
      bin: '544321',
      country: 'Mexico',
      country_code: 'MX',
      brand: 'Mastercard',
      issuer: 'BBVA México',
      quantity: faker.number.int({ min: 32, max: 85 }),
      volume_usd: faker.number.float({ min: 11000, max: 33000, fractionDigits: 2 })
    },
    {
      bin: '412987',
      country: 'Argentina',
      country_code: 'AR',
      brand: 'Visa',
      issuer: 'Banco Galicia',
      quantity: faker.number.int({ min: 15, max: 50 }),
      volume_usd: faker.number.float({ min: 5000, max: 20000, fractionDigits: 2 })
    },
    {
      bin: '559876',
      country: 'India',
      country_code: 'IN',
      brand: 'Mastercard',
      issuer: 'HDFC Bank',
      quantity: faker.number.int({ min: 38, max: 95 }),
      volume_usd: faker.number.float({ min: 13000, max: 36000, fractionDigits: 2 })
    }
  ]

  // Group by merchant
  const merchantMap = new Map<string, { transactions: number, volume_usd: number, name: string, uuid: string }>()
  for (const t of filtered) {
    const current = merchantMap.get(t.merchant.id) || {
      transactions: 0,
      volume_usd: 0,
      name: t.merchant.name,
      uuid: t.merchant.merchant_uuid
    }
    merchantMap.set(t.merchant.id, {
      ...current,
      transactions: current.transactions + 1,
      volume_usd: current.volume_usd + t.usd_amount
    })
  }

  const merchantsSorted = Array.from(merchantMap.entries())
    .map(([id, data]) => ({
      merchant_id: Number.parseInt(id),
      merchant_uuid: data.uuid,
      merchant_name: data.name,
      transactions: data.transactions,
      volume_usd: data.volume_usd,
      percentage: 0 // Will calculate below
    }))
    .sort((a, b) => b.volume_usd - a.volume_usd)

  const totalVolume = filtered.reduce((sum, t) => sum + t.usd_amount, 0)
  merchantsSorted.forEach((m) => {
    m.percentage = Number(((m.volume_usd / totalVolume) * 100).toFixed(1))
  })

  // Group by wallet
  const walletMap = new Map<string, { transactions: number, volume_usd: number, name: string, uuid: string }>()
  for (const t of filtered) {
    const current = walletMap.get(t.wallet.id) || {
      transactions: 0,
      volume_usd: 0,
      name: t.wallet.name,
      uuid: t.wallet.wallet_uuid
    }
    walletMap.set(t.wallet.id, {
      ...current,
      transactions: current.transactions + 1,
      volume_usd: current.volume_usd + t.usd_amount
    })
  }

  const walletsSorted = Array.from(walletMap.entries())
    .map(([id, data]) => ({
      wallet_id: Number.parseInt(id),
      wallet_uuid: data.uuid,
      wallet_name: data.name,
      transactions: data.transactions,
      volume_usd: data.volume_usd,
      percentage: Number(((data.volume_usd / totalVolume) * 100).toFixed(1))
    }))
    .sort((a, b) => b.volume_usd - a.volume_usd)

  return {
    date_start: dateStart || new Date(2025, 11, 14).toISOString().split('T')[0]!,
    date_end: dateEnd || new Date(2026, 0, 13).toISOString().split('T')[0]!,
    total_transactions: filtered.length,
    approved: {
      qty: paid.length,
      volume: Number(paidVolume.toFixed(2)),
      currency: 'USD'
    },
    pending: {
      qty: pending.length,
      volume: Number(pendingVolume.toFixed(2)),
      currency: 'USD'
    },
    error: {
      qty: error.length,
      volume: Number(errorVolume.toFixed(2)),
      currency: 'USD'
    },
    total_volume_usd: Number(totalVolume.toFixed(2)),
    volume_by_currency: volumeByCurrency,
    top_sale: {
      amount: topSale?.usd_amount || 0,
      charge_uuid: topSale?.charge_uuid || ''
    },
    worst_sale: {
      amount: worstSale?.usd_amount || 0,
      charge_uuid: worstSale?.charge_uuid || ''
    },
    payment_methods,
    volume_by_flag,
    top_bins,
    top_merchants: merchantsSorted.slice(0, 5),
    worst_merchants: merchantsSorted.slice(-3).reverse(),
    top_wallets: walletsSorted.slice(0, 5),
    worst_wallets: walletsSorted.slice(-3).reverse()
  }
}

/**
 * Generate approval rate dashboard
 */
export function generateApprovalRateDashboard(
  dateStart?: string,
  dateEnd?: string,
  onlyApprovalRate = false
): ApprovalRateSimple | ApprovalRateDashboard {
  const transactions = getTransactions()

  // Filter by date
  let filtered = transactions
  if (dateStart || dateEnd) {
    filtered = transactions.filter((t) => {
      const created = new Date(t.created_at)
      if (dateStart && created < new Date(dateStart)) return false
      if (dateEnd && created > new Date(dateEnd)) return false
      return true
    })
  }

  const approved = filtered.filter(t => !['error', 'issued', 'canceled'].includes(t.status))
  const overall_approval_rate = filtered.length > 0
    ? Number(((approved.length / filtered.length) * 100).toFixed(1))
    : 0

  const simple: ApprovalRateSimple = {
    date_start: dateStart || new Date(2025, 11, 14).toISOString().split('T')[0]!,
    date_end: dateEnd || new Date(2026, 0, 13).toISOString().split('T')[0]!,
    overall_approval_rate
  }

  if (onlyApprovalRate) {
    return simple
  }

  const approvedIds = new Set(approved.map(t => t.id))
  const merchantApprovalMap = new Map<string, { total: number, approved: number, name: string, uuid: string }>()
  for (const t of filtered) {
    const current = merchantApprovalMap.get(t.merchant.id) || {
      total: 0,
      approved: 0,
      name: t.merchant.name,
      uuid: t.merchant.merchant_uuid
    }
    merchantApprovalMap.set(t.merchant.id, {
      ...current,
      total: current.total + 1,
      approved: current.approved + (approvedIds.has(t.id) ? 1 : 0)
    })
  }

  const merchantsByApproval = Array.from(merchantApprovalMap.values())
    .map(item => ({
      name: item.name,
      merchant_uuid: item.uuid,
      approval_rate: item.total > 0 ? Number(((item.approved / item.total) * 100).toFixed(1)) : 0
    }))
    .sort((a, b) => b.approval_rate - a.approval_rate)

  const bestMerchant = merchantsByApproval[0] ?? { name: 'Unknown', merchant_uuid: undefined, approval_rate: 0 }
  const worstMerchant = merchantsByApproval[merchantsByApproval.length - 1] ?? { name: 'Unknown', merchant_uuid: undefined, approval_rate: 0 }

  // Generate full dashboard
  const rejected = filtered.filter(t => ['error', 'issued', 'canceled'].includes(t.status))
  const dashboard: ApprovalRateDashboard = {
    ...simple,
    total_transactions: filtered.length,
    approved_count: approved.length,
    rejected_count: rejected.length,
    best_acquirer: {
      name: 'Cielo',
      approval_rate: 92.3
    },
    worst_acquirer: {
      name: 'PagSeguro',
      approval_rate: 78.5
    },
    best_merchant: bestMerchant,
    worst_merchant: worstMerchant,
    approval_rate_trend: Array.from({ length: 30 }, (_, i) => {
      const date = new Date(2026, 0, 15 - (29 - i))
      return {
        date: date.toISOString().split('T')[0]!,
        approval_rate: faker.number.float({ min: 60, max: 88, fractionDigits: 1 }),
        total: faker.number.int({ min: 100, max: 500 }),
        approved: faker.number.int({ min: 80, max: 450 })
      }
    }),
    period_comparisons: {
      yesterday_vs_today: {
        current_ar: 88.5,
        previous_ar: 86.2,
        change_percentage: 2.3
      },
      last_7d_vs_previous_7d: {
        current_ar: 87.8,
        previous_ar: 85.5,
        change_percentage: 2.3
      },
      last_30d_vs_previous_30d: {
        current_ar: 87.5,
        previous_ar: 86.1,
        change_percentage: 1.4
      }
    },
    top_rejection_reasons: [
      {
        code: '05',
        description: 'Insufficient funds',
        count: 45,
        percentage: 35.5,
        trend: -2.3
      },
      {
        code: '51',
        description: 'Limit exceeded',
        count: 32,
        percentage: 25.2,
        trend: 1.2
      }
    ],
    acquirer_performance: [
      {
        name: 'Cielo',
        approval_rate: 92.3,
        top_rejection_reason: 'bad_request_83',
        last_7d_change: 1.5,
        last_15d_change: 2.1
      },
      {
        name: 'Rede',
        approval_rate: 89.7,
        top_rejection_reason: 'bad_request_51',
        last_7d_change: -0.5,
        last_15d_change: 0.8
      }
    ],
    payment_method_performance: [
      {
        type: 'credit_card',
        volume: 1200,
        approval_rate: 89.5
      },
      {
        type: 'pix',
        volume: 450,
        approval_rate: 95.2
      }
    ]
  }

  return dashboard
}
